import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPerfumes = () => {
  const [perfumes, setPerfumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPerfume, setEditingPerfume] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    collection: 'iconiques',
    notes: '',
    prices: { '10ml': 10, '50ml': 25 },
    refillable: true,
    image_url: '',
  });

  useEffect(() => {
    fetchPerfumes();
  }, []);

  const fetchPerfumes = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/perfumes`);
      setPerfumes(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des parfums');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPerfume) {
        await axios.put(`${API}/perfumes/${editingPerfume.id}`, formData, { withCredentials: true });
        toast.success('Parfum mis à jour');
      } else {
        await axios.post(`${API}/perfumes`, formData, { withCredentials: true });
        toast.success('Parfum ajouté');
      }
      fetchPerfumes();
      resetForm();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce parfum ?')) return;
    try {
      await axios.delete(`${API}/perfumes/${id}`, { withCredentials: true });
      toast.success('Parfum supprimé');
      fetchPerfumes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      collection: 'iconiques',
      notes: '',
      prices: { '10ml': 10, '50ml': 25 },
      refillable: true,
      image_url: '',
    });
    setEditingPerfume(null);
    setShowModal(false);
  };

  const openEdit = (perfume) => {
    setFormData(perfume);
    setEditingPerfume(perfume);
    setShowModal(true);
  };

  return (
    <AdminLayout title="Gestion des Parfums">
      <button
        onClick={() => setShowModal(true)}
        className="btn-primary px-6 py-3 rounded-full mb-6 inline-flex items-center gap-2"
        data-testid="add-perfume-btn"
      >
        <Plus size={20} />
        Ajouter un parfum
      </button>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <div className="grid gap-4">
          {perfumes.map((perfume) => (
            <div key={perfume.id} className="bg-white p-4 rounded-lg border flex justify-between items-start" data-testid={`perfume-item-${perfume.id}`}>
              <div>
                <h3 className="text-lg font-medium text-[#5C2018]">{perfume.name}</h3>
                <p className="text-sm text-[#332724]">{perfume.description}</p>
                <span className="text-xs text-[#B8922A]">{perfume.collection}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(perfume)} className="p-2 text-[#1E7A6A] hover:bg-[#1E7A6A]/10 rounded" data-testid={`edit-perfume-${perfume.id}`}>
                  <Edit2 size={18} />
                </button>
                <button onClick={() => handleDelete(perfume.id)} className="p-2 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-perfume-${perfume.id}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={resetForm}>
          <div className="bg-white p-8 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="perfume-modal">
            <h2 className="text-2xl mb-4">{editingPerfume ? 'Modifier' : 'Ajouter'} un parfum</h2>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Nom"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full p-2 border rounded mb-3"
                required
                data-testid="perfume-name-input"
              />
              <textarea
                placeholder="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border rounded mb-3"
                rows="3"
                required
              />
              <select
                value={formData.collection}
                onChange={(e) => setFormData({ ...formData, collection: e.target.value })}
                className="w-full p-2 border rounded mb-3"
              >
                <option value="iconiques">Les Iconiques</option>
                <option value="pepites">Les Pépites</option>
                <option value="audacieux">Les Audacieux</option>
              </select>
              <input
                type="text"
                placeholder="Notes olfactives"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full p-2 border rounded mb-3"
                required
              />
              <input
                type="url"
                placeholder="URL de l'image"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full p-2 border rounded mb-3"
              />
              <div className="flex gap-3 mb-4">
                <button type="submit" className="btn-primary px-6 py-2 rounded-full" data-testid="perfume-submit">
                  Enregistrer
                </button>
                <button type="button" onClick={resetForm} className="btn-secondary px-6 py-2 rounded-full">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPerfumes;
